import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn } from "@/components/common";
import { MONTHS } from "@/lib/calendarUtils";
import { Printer, Download, Ship, PlaneLanding, PlaneTakeoff, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import SpeedboatSchedule from "@/components/SpeedboatSchedule";

export default function Trasferimenti() {
  const { isAdmin } = useAuth();
  const _now = new Date();
  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const d = new Date(_now.getFullYear(), _now.getMonth() + i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        return { key, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [month, setMonth] = useState(monthOptions[0].key);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [urgent, setUrgent] = useState({ today: [], tomorrow: [], todayIso: "", tomorrowIso: "" });

  useEffect(() => {
    setLoading(true);
    api
      .get("/transfers", { params: { month } })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [month]);

  // Arrivi/partenze imminenti (oggi e domani), indipendenti dal mese selezionato
  useEffect(() => {
    const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    const d = new Date();
    const today = iso(d);
    const tm = new Date(d);
    tm.setDate(tm.getDate() + 1);
    const tomorrow = iso(tm);
    const months = [...new Set([today.slice(0, 7), tomorrow.slice(0, 7)])];
    Promise.all(
      months.map((m) => api.get("/transfers", { params: { month: m } }).then((r) => r.data.rows || []).catch(() => []))
    ).then((all) => {
      const flat = all.flat();
      setUrgent({
        today: flat.filter((r) => r.date === today),
        tomorrow: flat.filter((r) => r.date === tomorrow),
        todayIso: today,
        tomorrowIso: tomorrow,
      });
    });
  }, []);

  const monthLabel = monthOptions.find((o) => o.key === month)?.label || month;
  const arrivi = rows.filter((r) => r.type === "arrivo").length;
  const partenze = rows.filter((r) => r.type === "partenza").length;

  // Posti speedboat per fascia oraria (quante persone per orario) → quante barche servono
  const slots = useMemo(() => {
    const agg = (type) => {
      const m = {};
      rows.filter((r) => r.type === type).forEach((r) => {
        const t = r.time || "—";
        if (!m[t]) m[t] = { pax: 0, count: 0 };
        m[t].pax += r.pax || 0;
        m[t].count += 1;
      });
      return Object.entries(m)
        .map(([time, v]) => ({ time, ...v }))
        .sort((a, b) => a.time.localeCompare(b.time));
    };
    return { arrivi: agg("arrivo"), partenze: agg("partenza") };
  }, [rows]);

  // Esporta la pagina tradotta in inglese (finestra stampabile / salvabile in PDF)
  const EN_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const enMonthLabel = `${EN_MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
  const exportEnglish = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const trs = rows
      .map(
        (r) => `<tr>
        <td class="${r.type === "arrivo" ? "arr" : "dep"}">${r.type === "arrivo" ? "Arrival" : "Departure"}</td>
        <td>${esc(r.date)}</td><td class="time">${esc(r.time || "—")}</td><td>${esc(r.flight || "—")}</td>
        <td>${esc(r.guest || "—")}</td><td class="c">${esc(r.pax)}</td>
        <td>${esc(r.room)}</td><td>${esc(r.structure)}</td></tr>`
      )
      .join("");
    const slotHtml = (list, title) =>
      `<div class="slotcard"><div class="slottitle">${title}</div>${
        list.length
          ? list.map((s) => `<span class="slot"><b>${esc(s.time)}</b> ${s.pax} seats · ${s.count} bkg</span>`).join("")
          : "<span class='muted'>—</span>"
      }</div>`;
    const html = `<!doctype html><html lang="en"><head><meta charset="utf-8"/>
      <title>Speedboat Transfers — ${esc(enMonthLabel)}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;margin:32px;}
        h1{font-size:20px;margin:0 0 4px;} .sub{color:#64748b;font-size:13px;margin:0 0 18px;}
        table{width:100%;border-collapse:collapse;font-size:13px;}
        th{background:#0f172a;color:#fff;text-align:left;padding:8px 10px;font-size:11px;text-transform:uppercase;letter-spacing:.03em;}
        td{padding:7px 10px;border-bottom:1px solid #e2e8f0;}
        .c{text-align:center;} .time{font-weight:bold;color:#0369a1;}
        .arr{color:#059669;font-weight:bold;} .dep{color:#b45309;font-weight:bold;}
        .slots{display:flex;gap:14px;flex-wrap:wrap;margin:0 0 18px;}
        .slotcard{border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px;min-width:220px;}
        .slottitle{font-size:11px;text-transform:uppercase;color:#64748b;font-weight:bold;margin-bottom:6px;}
        .slot{display:inline-block;background:#f1f5f9;border-radius:6px;padding:4px 8px;margin:2px;font-size:12px;}
        .muted{color:#94a3b8;}
        .btn{margin:18px 0;padding:10px 18px;background:#0369a1;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:14px;}
        @media print{.btn{display:none;}}
      </style></head><body>
      <h1>Speedboat Transfers</h1>
      <p class="sub">${esc(enMonthLabel)} · ${arrivi} arrivals · ${partenze} departures</p>
      <div class="slots">${slotHtml(slots.arrivi, "Seats per slot · Arrivals")}${slotHtml(slots.partenze, "Seats per slot · Departures")}</div>
      <button class="btn" onclick="window.print()">Print / Save as PDF</button>
      <table><thead><tr>
        <th>Type</th><th>Date</th><th>Speedboat time</th><th>Flight</th><th>Guest</th><th>Pax / Seats</th><th>Room</th><th>Property</th>
      </tr></thead><tbody>${trs || `<tr><td colspan="8" style="text-align:center;color:#94a3b8;padding:24px;">No transfers</td></tr>`}</tbody></table>
      </body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const w = window.open(url, "_blank");
    if (!w) {
      URL.revokeObjectURL(url);
      toast.error("Consenti i popup per esportare la pagina in inglese");
      return;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div>
      <PageHeader title="Trasferimenti" subtitle="Speedboat · arrivi e partenze staff">
        <div className="flex items-center gap-2 flex-wrap no-print">
          <select
            data-testid="tr-month-year"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white outline-none focus:ring-2 focus:ring-sky-500 font-medium"
          >
            {monthOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
          <Btn data-testid="tr-print" variant="secondary" onClick={() => window.print()}>
            <Printer size={16} /> Stampa
          </Btn>
          <Btn data-testid="tr-export-en" onClick={exportEnglish}>
            <Download size={16} /> Esporta (EN)
          </Btn>
        </div>
      </PageHeader>

      <div className="hidden print:block mb-3">
        <div className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Ship size={18} /> Trasferimenti Speedboat — {monthLabel}
        </div>
      </div>

      {isAdmin && <SpeedboatSchedule />}

      {/* Mini-avviso: arrivi/partenze imminenti (oggi e domani) */}
      <div className="grid sm:grid-cols-2 gap-3 mb-5 no-print" data-testid="tr-urgent">
        {[
          { key: "today", label: "Oggi", iso: urgent.todayIso, list: urgent.today },
          { key: "tomorrow", label: "Domani", iso: urgent.tomorrowIso, list: urgent.tomorrow },
        ].map(({ key, label, iso, list }) => (
          <div key={key} className="rounded-lg border border-sky-200 bg-sky-50/50 p-3" data-testid={`tr-urgent-${key}`}>
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-sky-800 mb-2">
              <CalendarClock size={14} /> {label} <span className="text-sky-500 font-medium normal-case">· {iso}</span>
            </div>
            {list.length === 0 ? (
              <div className="text-slate-400 text-sm">Nessun trasferimento</div>
            ) : (
              <ul className="space-y-1">
                {list
                  .slice()
                  .sort((a, b) => (a.time || "").localeCompare(b.time || ""))
                  .map((r, i) => (
                    <li key={`${r.booking_id}-${r.type}-${i}`} className="flex items-center gap-2 text-sm">
                      {r.type === "arrivo" ? (
                        <PlaneLanding size={13} className="text-emerald-600 shrink-0" />
                      ) : (
                        <PlaneTakeoff size={13} className="text-amber-600 shrink-0" />
                      )}
                      <span className="font-bold text-sky-700 w-14 shrink-0">{r.time || "—"}</span>
                      <span className="text-slate-800 truncate">{r.guest || "—"}</span>
                      {r.flight && <span className="text-xs font-semibold text-slate-600 shrink-0">✈ {r.flight}</span>}
                      <span className="text-slate-400 text-xs shrink-0">· {r.pax}p · {r.room}</span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-4 mb-4 text-sm no-print">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold" data-testid="tr-count-arrivi">
          <PlaneLanding size={15} /> {arrivi} arrivi
        </span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 font-semibold" data-testid="tr-count-partenze">
          <PlaneTakeoff size={15} /> {partenze} partenze
        </span>
      </div>

      {/* Posti speedboat per fascia oraria */}
      <div className="grid sm:grid-cols-2 gap-3 mb-5" data-testid="tr-slots">
        {[
          { key: "arrivi", title: "Posti speedboat · Arrivi", Icon: PlaneLanding, cls: "emerald" },
          { key: "partenze", title: "Posti speedboat · Partenze", Icon: PlaneTakeoff, cls: "amber" },
        ].map(({ key, title, Icon, cls }) => (
          <div key={key} className={`rounded-lg border p-3 bg-white ${cls === "emerald" ? "border-emerald-200" : "border-amber-200"}`} data-testid={`tr-slots-${key}`}>
            <div className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-2 ${cls === "emerald" ? "text-emerald-700" : "text-amber-700"}`}>
              <Icon size={14} /> {title}
            </div>
            {slots[key].length === 0 ? (
              <div className="text-slate-300 text-sm">—</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {slots[key].map((s) => (
                  <div key={s.time} className={`flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md ${cls === "emerald" ? "bg-emerald-50" : "bg-amber-50"}`}>
                    <span className="font-bold text-sky-700 text-sm">{s.time}</span>
                    <span className="text-slate-800 font-semibold text-sm">{s.pax} posti</span>
                    <span className="text-slate-400 text-xs">· {s.count} pren.</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="tr-table">
          <thead>
            <tr className="bg-slate-50 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">
              <th className="px-3 py-2.5">Tipo</th>
              <th className="px-3 py-2.5">Data</th>
              <th className="px-3 py-2.5">Ora speedboat</th>
              <th className="px-3 py-2.5">Volo</th>
              <th className="px-3 py-2.5">Cliente</th>
              <th className="px-3 py-2.5 text-center">Pax / Sedute</th>
              <th className="px-3 py-2.5">Camera</th>
              <th className="px-3 py-2.5">Struttura</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">Caricamento…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400" data-testid="tr-empty">Nessun trasferimento in {monthLabel}</td></tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.booking_id}-${r.type}-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tr-row-${r.type}`}>
                  <td className="px-3 py-2.5">
                    {r.type === "arrivo" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-700"><PlaneLanding size={12} /> Arrivo</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700"><PlaneTakeoff size={12} /> Partenza</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-slate-800">{r.date}</td>
                  <td className="px-3 py-2.5 font-bold text-sky-700">{r.time || <span className="text-slate-300 font-normal">—</span>}</td>
                  <td className="px-3 py-2.5 font-semibold text-slate-700" data-testid={`tr-flight-${r.booking_id}-${r.type}`}>{r.flight || <span className="text-slate-300 font-normal">—</span>}</td>
                  <td className="px-3 py-2.5 text-slate-800">{r.guest || "—"}</td>
                  <td className="px-3 py-2.5 text-center font-semibold text-slate-800">{r.pax}</td>
                  <td className="px-3 py-2.5 text-slate-600">{r.room}</td>
                  <td className="px-3 py-2.5 text-slate-500">{r.structure}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
